import { createApp } from "vue";
import { createPinia } from "pinia";
import { VueQueryPlugin, QueryClient } from "@tanstack/vue-query";
import { vuetify } from "./plugins/vuetify";
import router from "./router";
import {
  isChunkLoadErrorMessage,
  scheduleRecoverFromStaleChunkPreload,
} from "@/utils/chunkRecovery";
import App from "./App.vue";
import "./styles/main.scss";
import { usePushNotifications } from "./composables/usePushNotifications";
import * as Sentry from "@sentry/capacitor";
import type { CapacitorOptions } from "@sentry/capacitor";
import * as SentryVue from "@sentry/vue";
import { Capacitor } from "@capacitor/core";
import { authService } from "@/services/authService";
import { AUTH_USER_REFRESHED_EVENT } from "@/services/authUserProfile";
import { versionManager } from "@/services/versionManager";
import type { AuthUser } from "@/types";

const DEFAULT_SENTRY_DSN =
  "https://e38578fbb606f58c4050f000b15633f1@o4505035921096704.ingest.us.sentry.io/4511181200228352";
const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim() || DEFAULT_SENTRY_DSN;
const sentryEnvironment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() ||
  import.meta.env.MODE ||
  "production";
const sentryRelease = import.meta.env.VITE_APP_VERSION || undefined;
const sentryBuildTime = import.meta.env.VITE_BUILD_TIME || undefined;
const VERSION_RELOAD_GUARD_KEY = "app_version_reload_guard";
const VERSION_RELOAD_GUARD_MS = 60_000;

function resolveSentryTracesSampleRate(): number | undefined {
  const rawValue = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  if (!rawValue) {
    return undefined;
  }

  const parsedValue = Number.parseFloat(rawValue);
  if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 1) {
    console.warn(
      "[Sentry] Ignoring invalid VITE_SENTRY_TRACES_SAMPLE_RATE value:",
      rawValue,
    );
    return undefined;
  }

  return parsedValue;
}

function syncSentryUser(user: AuthUser | null): void {
  if (import.meta.env.DEV || !sentryDsn) {
    return;
  }

  Sentry.setUser(
    user
      ? {
          id: user.ulid,
          username: user.username || undefined,
          email: user.email || undefined,
        }
      : null,
  );
}

function initializeSentry(vueApp: ReturnType<typeof createApp>): void {
  if (import.meta.env.DEV) {
    console.log("[Sentry] Dev environment — skipping Sentry initialization");
    return;
  }

  if (!sentryDsn) {
    return;
  }

  const sentryOptions: CapacitorOptions &
    NonNullable<Parameters<typeof SentryVue.init>[0]> = {
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    app: vueApp,
    sendDefaultPii: true,
  };

  const tracesSampleRate = resolveSentryTracesSampleRate();
  if (tracesSampleRate !== undefined) {
    sentryOptions.tracesSampleRate = tracesSampleRate;
    sentryOptions.integrations = [
      SentryVue.browserTracingIntegration({
        router,
      }),
    ];
  }

  Sentry.init(sentryOptions, SentryVue.init);
  Sentry.setTag("platform", Capacitor.getPlatform());
  if (sentryRelease) {
    Sentry.setTag("app_version", sentryRelease);
  }
  if (sentryBuildTime) {
    Sentry.setTag("build_time", sentryBuildTime);
  }
  Sentry.setContext("app", {
    platform: Capacitor.getPlatform(),
    isNativePlatform: Capacitor.isNativePlatform(),
    environment: sentryEnvironment,
    release: sentryRelease ?? "unknown",
    buildTime: sentryBuildTime ?? "unknown",
  });

  syncSentryUser(authService.getStoredUser());

  if (typeof window !== "undefined") {
    window.addEventListener(AUTH_USER_REFRESHED_EVENT, (event) => {
      const authEvent = event as CustomEvent<AuthUser>;
      syncSentryUser(authEvent.detail ?? null);
    });
  }
}

function registerGlobalErrorHandlers(): void {
  if (typeof window === "undefined") {
    return;
  }

  // Install preload recovery before the first route resolves so initial lazy-load
  // failures are caught during bootstrap, not only after the app has mounted.
  window.addEventListener("vite:preloadError", (event) => {
    const err = (event as VitePreloadErrorEvent).payload;
    if (!isChunkLoadErrorMessage(String(err?.message ?? err))) return;
    event.preventDefault();
    scheduleRecoverFromStaleChunkPreload();
  });

  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    const message = error instanceof Error ? error.message : String(error);

    console.error("[Unhandled Promise Rejection]", {
      message,
      error,
      stack: error instanceof Error ? error.stack : undefined,
      route: router.currentRoute?.value?.fullPath || "unknown",
    });

    if (isChunkLoadErrorMessage(message)) {
      console.warn(
        "[Navigation] Chunk load failure detected via unhandled rejection. This may cause navigation issues.",
      );
    }
  });

  window.addEventListener("error", (event) => {
    if (event.error) {
      console.error("[Global Error]", {
        message: event.message,
        error: event.error,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        route: router.currentRoute?.value?.fullPath || "unknown",
      });
    }
  });
}

async function handleVersionBootstrap(): Promise<boolean> {
  try {
    const versionChanged = await versionManager.initialize();
    if (!versionChanged || typeof window === "undefined") {
      return false;
    }

    const lastReloadAt = sessionStorage.getItem(VERSION_RELOAD_GUARD_KEY);
    const lastReloadMs = lastReloadAt ? Number.parseInt(lastReloadAt, 10) : 0;
    const shouldReload =
      !lastReloadMs ||
      Number.isNaN(lastReloadMs) ||
      Date.now() - lastReloadMs > VERSION_RELOAD_GUARD_MS;

    if (shouldReload) {
      sessionStorage.setItem(VERSION_RELOAD_GUARD_KEY, Date.now().toString());
      window.location.reload();
      return true;
    }

    console.warn(
      "[Main] Version changed but reload guard blocked a second forced reload.",
    );
    return false;
  } catch (error) {
    console.error("[Main] Failed to initialize version manager:", error);
    return false;
  }
}

async function bootstrapApp(): Promise<void> {
  if (await handleVersionBootstrap()) {
    return;
  }

  registerGlobalErrorHandlers();

  const app = createApp(App);
  const pinia = createPinia();
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 2,
        retry: 3,
        refetchOnWindowFocus: true,
        refetchOnMount: false,
        refetchOnReconnect: true,
      },
    },
  });

  app.config.warnHandler = (msg, instance, trace) => {
    const suppressedWarnings = [
      "onScopeDispose() is called when there is no active effect scope",
      "Missing ref owner context",
    ];

    if (suppressedWarnings.some((warning) => msg.includes(warning))) {
      return;
    }

    console.warn(`[Vue warn]: ${msg}${trace}`);
  };

  app.config.errorHandler = (err, instance, info) => {
    const componentName =
      instance?.$options?.name || instance?.$.type?.name || "Unknown";
    const routePath = router.currentRoute?.value?.fullPath || "unknown";

    console.error(`[Vue error in ${componentName}]: ${info}`, {
      error: err,
      message: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
      route: routePath,
      componentName,
      info,
    });

    if (sentryDsn) {
      Sentry.withScope((scope) => {
        scope.setTag("error_source", "vue");
        scope.setContext("vue", {
          componentName,
          info,
          route: routePath,
        });
        Sentry.captureException(err);
      });
    }

    const errorCountKey = "vue_error_count";
    const lastErrorTimeKey = "vue_last_error_time";
    const currentCount = parseInt(
      sessionStorage.getItem(errorCountKey) || "0",
      10,
    );
    const lastErrorTime = parseInt(
      sessionStorage.getItem(lastErrorTimeKey) || "0",
      10,
    );
    const now = Date.now();

    if (now - lastErrorTime > 30_000) {
      sessionStorage.setItem(errorCountKey, "1");
    } else {
      sessionStorage.setItem(errorCountKey, String(currentCount + 1));
    }
    sessionStorage.setItem(lastErrorTimeKey, String(now));

    if (currentCount + 1 >= 5 && now - lastErrorTime < 30_000) {
      console.error(
        "[Vue] Multiple errors detected in quick succession - app may be in broken state. Consider refreshing.",
      );
    }
  };

  initializeSentry(app);

  app.use(pinia);
  app.use(vuetify);
  app.use(router);
  app.use(VueQueryPlugin, { queryClient });
  app.mount("#app");

  const pushNotifications = usePushNotifications();
  if (pushNotifications.isSupported.value) {
    pushNotifications.initialize().catch((error) => {
      console.error("[Main] Failed to initialize push notifications:", error);
    });
  }
}

void bootstrapApp();

// Force rebuild 20260113.001
